// Where the raid team IS while a raid runs, and how each member is standing.
//
// Pure geometry over the raid's own clock — no DOM, no canvas — so the whole
// motion model is testable headless. raidTeamAnim.js turns what this returns
// into pixels; raidMapAnim.js decides which tiles to draw it on.
//
// Two coordinate spaces, and they are easy to confuse:
//   GRID space — fractional (row, col) over the raid grid. 1 unit = one tile.
//                col is +x (right), row is +y (down), so a heading angle is
//                atan2(dRow, dCol) and screen-space rotation Just Works.
//   PARTY space — a member's offset from the party's own centre, in TILE
//                FRACTIONS (0.1 = a tenth of a tile edge). The caller scales
//                by whatever a tile is worth in pixels, so nothing in here
//                has to know that a tile canvas is 120px.

// Half a tile of walking. The party crosses a tile boundary in two of these —
// the second half of one room and the first half of the next — so ONE constant
// sets the walking speed everywhere on the map.
//
// It is tuned to the road: a road room's baseDuration is 10s and the clamp
// below turns that into a continuous 5s-per-half-tile traverse with no dwell
// at all. Gates and hazards run 60-120s, so they get the same 5s walk in, a
// long search, and the same 5s walk out. Rooms therefore differ in how long
// the team STAYS, never in how fast it moves — which is what makes a road read
// as a road rather than as a room the team happens to leave early.
export const WALK_HALF_MS = 5000;

// A room the team cannot cross at that pace in the time it lasts has no dwell
// phase at all: the two legs are squeezed until they meet and the team crosses
// without stopping. That is what a road IS — 10s against the ~10s it takes to
// walk a tile — and it is why road tiles need no special case anywhere here.

// How far a member has to walk from their place in the file to their post,
// when the tile does not say. Tiles vary a lot — a lakeside post is out on the
// bank, most posts are a stone's throw from the middle — so `partyAt` takes the
// real figure and this is only the fallback.
const SPREAD_TILES = 0.25;

/**
 * How long the team takes to fan out on arrival, and to gather back up before
 * leaving, for a spread of `tiles`.
 *
 * NOT a look-right number. It is the distance at the walking speed set above,
 * with the smoothstep's 1.5x peak factor allowed for, so **the fastest a member
 * ever moves while spreading out is the speed they walk at** — whatever the
 * tile asks them to spread across.
 *
 * It was a flat 1100ms, which put the peak at three and a half times walking
 * pace and read as three people being yanked apart. A flat figure large enough
 * for a lakeside post would have had the other tiles crawling instead. The
 * distance is the thing you can measure and the speed is the thing the eye
 * judges, so the duration is the one that gets to be derived.
 */
const settleFor = tiles => (Math.max(0.02, tiles) / 0.5) * WALK_HALF_MS * 1.5;

// ...and the spread happens while the party is STANDING STILL, in the dwell
// either side, rather than over the last stretch of the walk in. Overlapping
// the two added the walk's own velocity to the spread's, so the one moment the
// team was meant to settle was the moment it moved fastest.

const TAU = Math.PI * 2;
const clamp01 = v => (v < 0 ? 0 : v > 1 ? 1 : v);
const mix = (a, b, t) => a + (b - a) * t;
// Smoothstep. Zero slope at both ends, which is the whole point: it is what
// keeps a blend from starting and stopping abruptly.
const smooth = t => t * t * (3 - 2 * t);

/** Wrap an angle into (-PI, PI]. */
export function normAngle(a) {
    let x = a % TAU;
    if (x > Math.PI) x -= TAU;
    if (x <= -Math.PI) x += TAU;
    return x;
}

/**
 * Shortest-arc blend between two angles, canonically wrapped.
 *
 * The wrap is the point: a torch sweeping through north must not take the
 * long way round to get there, and blending raw radians is exactly how it
 * would. The result is normalised so `t = 1` gives back `b` itself rather
 * than `b` plus whatever multiple of a turn `a` happened to carry.
 */
export function mixAngle(a, b, t) {
    let d = (b - a) % TAU;
    if (d > Math.PI) d -= TAU;
    if (d < -Math.PI) d += TAU;
    return normAngle(a + d * t);
}

/**
 * The party's position and bearing partway through one path step.
 *
 * @param path   the raid's selectedPath — objects carrying {row, col}
 * @param index  which step the team is currently in (raid.currentRoomIndex)
 * @param elapsedMs  how long they have been in it
 * @param durationMs the step's total length (room.baseDuration * 1000)
 * @param stage  what THIS tile makes of the party — `{ spread, anchor }` from
 *               its ground plan. `spread` is how far the members have to walk
 *               to their posts (it times the fan-out); `anchor` is where the
 *               party gathers, in tile fractions from the middle. Defaults put
 *               the team in the middle of the tile, which is right everywhere
 *               that is not a lake or a solid block of building.
 * @returns {{row, col, heading, gait, walking}}
 *   row/col   fractional GRID position
 *   heading   radians; which way they are facing/travelling
 *   gait      1 = marching, 0 = stopped and searching (the pose blend)
 *   walking   true while the position is actually changing
 *
 * The handoff between steps is the midpoint of the shared edge: the walk out
 * of step i ends exactly where the walk into step i+1 begins, so the team
 * crosses the seam without a jump even though each step computes its own
 * position in ignorance of the other. **That holds whatever the anchor does**,
 * because the anchor only moves the middle of the journey, never its ends.
 */
export function partyAt(path, index, elapsedMs, durationMs, stage = null) {
    const n = path?.length || 0;
    if (!n) return { row: 0, col: 0, heading: 0, gait: 0, walking: false };
    const i = Math.max(0, Math.min(n - 1, index | 0));
    const A = path[i];
    const P = i > 0 ? path[i - 1] : null;
    const N = i + 1 < n ? path[i + 1] : null;

    const dur = Math.max(0, durationMs || 0);
    // A room the team has overstayed (the raid loop ticks at 1Hz, so a step can
    // be up to a second past due before the index flips) holds them at the seam
    // rather than extrapolating past it — walking on and then being yanked back
    // when the tick lands is worse than marching on the spot for a moment.
    const e = Math.max(0, Math.min(dur, elapsedMs || 0));

    // Where they gather, and therefore how far each leg of the crossing is.
    // On almost every tile this is the middle and both legs are half a tile,
    // which is the case every constant in here was tuned against.
    const anchor = stage?.anchor;
    const aRow = A.row + (anchor?.y || 0), aCol = A.col + (anchor?.x || 0);
    // Each leg is timed by its OWN length, so the pace is the same on a tile
    // the team walks straight across and one where it has to come round a lake.
    // A leg that will not fit in the room's own clock is scaled down with its
    // partner rather than on its own — a road's two halves have to keep meeting
    // in the middle, and that is what makes it a continuous traverse.
    const legIn = P ? Math.hypot(aRow - (A.row + P.row) / 2, aCol - (A.col + P.col) / 2) : 0;
    const legOut = N ? Math.hypot((A.row + N.row) / 2 - aRow, (A.col + N.col) / 2 - aCol) : 0;
    let wIn = legIn / WALK_SPEED, wOut = legOut / WALK_SPEED;
    if (wIn + wOut > dur && wIn + wOut > 0) {
        const squeeze = dur / (wIn + wOut);
        wIn *= squeeze;
        wOut *= squeeze;
    }
    // How long they actually stand on this tile, and how much of that each end
    // of it spends spreading out and gathering back up. A road's dwell is zero,
    // so its settle is zero and the team marches straight through in file —
    // still no road special case anywhere.
    const dwell = Math.max(0, dur - wIn - wOut);
    const settle = Math.min(settleFor(stage?.spread ?? SPREAD_TILES), dwell / 2);

    // The two bearings this tile has: the one they arrive on and the one they
    // leave on. They differ wherever the path turns, and the whole tile has to
    // get from one to the other WITHOUT a step in it — see the dwell below.
    const inHeading = P ? Math.atan2(A.row - P.row, A.col - P.col) : null;
    const outHeading = N ? Math.atan2(N.row - A.row, N.col - A.col) : null;

    // ...so the team TURNS ACROSS THE TILE rather than pivoting at some instant
    // inside it. Interpolating over the whole step is what makes this continuous
    // everywhere at once: it leaves at `inHeading`, which is the bearing the
    // previous step arrived on, and reaches `outHeading` exactly as the next
    // step begins on it. A tile the path runs straight through has the two
    // equal and nothing rotates at all.
    //
    // Every earlier attempt pinned the turn to a phase boundary — arrival,
    // then the dwell — and each one moved the jump somewhere else instead of
    // removing it. A road has no dwell to hide a turn in, which is what finally
    // made that obvious.
    let row = aRow, col = aCol;
    let heading = (inHeading != null && outHeading != null)
        ? mixAngle(inHeading, outHeading, dur > 0 ? e / dur : 1)
        : (outHeading ?? inHeading ?? 0);
    let walking = false;
    // Marching unless they are standing still — see the settle ramps below.
    let gait = 1;

    if (wIn > 0 && e < wIn) {
        // walking in from the shared edge with the previous tile
        const k = e / wIn;
        row = mix((A.row + P.row) / 2, aRow, k);
        col = mix((A.col + P.col) / 2, aCol, k);
        walking = true;
    } else if (wOut > 0 && e >= dur - wOut) {
        // `>=` rather than `>` so the two walk windows TILE the duration when
        // the squeeze makes them meet in the middle — a road would otherwise
        // have a single frame of standing still at its exact midpoint.
        // walking out toward the shared edge with the next tile
        const k = (e - (dur - wOut)) / wOut;
        row = mix(aRow, (A.row + N.row) / 2, k);
        col = mix(aCol, (A.col + N.col) / 2, k);
        walking = true;
    } else if (settle > 0) {
        // Standing on the tile. They arrive in file and fan out over `settle`,
        // hold their posts, then gather back into file in time to leave — so
        // the formation only ever changes while the party's own centre is
        // still, and the walk's velocity is never added to the spread's.
        const into = e - wIn;
        gait = clamp01(into < settle ? 1 - into / settle
            : into > dwell - settle ? 1 - (dwell - into) / settle
                : 0);
    } else {
        gait = 0;   // a dwell too short to spread out in: just stand there
    }
    return { row, col, heading, gait, walking };
}

/**
 * Elapsed/duration for the step the raid is currently in.
 *
 * roomStartTime and nextRoomTime are the raid's own schedule and they MOVE —
 * a room-time consumable slides both — so the window is read off them rather
 * than recomputed from baseDuration. The entry portal has neither (it is
 * scheduled by initRaid, not by enterNextRoom) and takes no time anyway.
 */
export function stepClock(raid, now) {
    const start = raid?.roomStartTime ?? raid?.startTime ?? now;
    const end = raid?.nextRoomTime ?? start;
    return { elapsedMs: now - start, durationMs: Math.max(0, end - start) };
}

// ============================================================================
// SEARCH POSTS — where on a tile a member actually stands
// ============================================================================
// Until now the team searched from a slowly turning ring about the tile's
// centre, which is a shape, not a place: it put people on roofs, and on the
// water tile it put all three of them in the lake.
//
// A post is a spot on the tile that a person could really stand on, picked
// once per tile from the ground plan the renderer now publishes. Members walk
// out to their posts on arrival and potter about them until it is time to
// leave. Fixing the post rather than continuously projecting a moving ring out
// of the obstacles is the whole trick: a projection is discontinuous wherever
// the nearest way out of a building flips from one wall to another, and this
// animation has already been bitten twice by exactly that class of bug.

// How close to a wall or a waterline a post may sit. A figure is ~6px wide in
// a 120px tile, so this is a comfortable body's width clear of it.
const POST_CLEARANCE = 0.055;

// Rings the candidates are drawn from, as a fraction of the tile. Far enough
// apart to look like three people covering an area, near enough that the team
// still reads as a team. The rings widen when the inner ones are built over.
//
// The outermost is 0.42 for a specific reason: the lake's bank sits at 0.35 and
// a post needs POST_CLEARANCE beyond that, which leaves a walkable band from
// 0.405 to the 0.445 where a figure starts hanging into the next room. There
// is one ring's worth of dry land on a water tile and this is it. (Diagonally
// there is far more, and the golden-angle sweep finds it first.)
const POST_RINGS = [0.20, 0.30, 0.42];
const POST_ANGLES = 16;

/**
 * Turn the renderer's ground plan into the space the party works in: tile
 * FRACTIONS, with the origin at the tile's centre rather than its corner.
 *
 * @param plan  what roomImageGen put on `tile.plan` — pixels, origin top-left
 * @param tilePx  the tile canvas size those pixels are in
 */
export function normalisePlan(plan, tilePx) {
    if (!plan || !tilePx) return null;
    const f = v => v / tilePx - 0.5;
    return {
        blocks: (plan.blocks || []).map(b => ({ x0: f(b.x0), y0: f(b.y0), x1: f(b.x1), y1: f(b.y1) })),
        water: plan.water
            ? { cx: f(plan.water.cx), cy: f(plan.water.cy), r: plan.water.r / tilePx, bank: plan.water.bank / tilePx }
            : null,
        focus: plan.focus ? { x: f(plan.focus.x), y: f(plan.focus.y) } : null,
    };
}

// How tightly the team gathers round a campfire. Close enough to be sharing
// it, far enough not to be standing in it.
const CAMP_RINGS = [0.085, 0.125, 0.17];

/** Is (x, y) somewhere a person could stand, given `clear` of elbow room? */
export function standable(plan, x, y, clear = POST_CLEARANCE) {
    if (Math.abs(x) > 0.5 - clear || Math.abs(y) > 0.5 - clear) return false;
    const w = plan?.water;
    // The bank is walkable shingle; the water itself is not.
    if (w && Math.hypot(x - w.cx, y - w.cy) < w.bank + clear) return false;
    for (const b of plan?.blocks || []) {
        if (x > b.x0 - clear && x < b.x1 + clear && y > b.y0 - clear && y < b.y1 + clear) return false;
    }
    return true;
}

/**
 * Pick a standing post for each member, in tile fractions from the centre.
 *
 * Deterministic in (plan, count, seed), so a member holds the same post for
 * the whole visit and across every frame of it — which is what keeps the
 * motion continuous without any smoothing at all.
 *
 * Candidates come off concentric rings and are taken in order of a seeded
 * angular sweep, each one having to clear those already taken, so three people
 * end up spread around the tile rather than shoulder to shoulder. The rings
 * widen outward, so a tile that is mostly building or mostly lake pushes the
 * team to its edges by itself — no per-room-type rule anywhere.
 */
export function searchPosts(count, plan, seed = 0, opts = {}) {
    const n = Math.max(0, count | 0);
    if (!n) return [];
    // Where the ring of candidates is centred, and how wide it runs. A camp
    // gathers round its fire; everywhere else works outward from the middle of
    // the tile. Same routine either way — the tile just says where and how far.
    const around = opts.around || { x: 0, y: 0 };
    const rings = opts.rings || POST_RINGS;
    const spacing = opts.spacing ?? [0.17, 0.10, 0];
    const phase = ((seed % 360) / 360) * TAU;
    const taken = [];
    // A post has to be this far from the ones already taken. Relaxed if the
    // walkable ground is too tight to honour it — better crowded than nobody.
    for (const apart of spacing) {
        for (const radius of rings) {
            for (let a = 0; a < POST_ANGLES && taken.length < n; a++) {
                // Golden-angle order rather than sweeping round, so the first
                // few candidates tried are already spread out
                const ang = phase + a * 2.39996;
                const x = around.x + Math.cos(ang) * radius, y = around.y + Math.sin(ang) * radius;
                if (!standable(plan, x, y)) continue;
                if (taken.some(p => Math.hypot(p.x - x, p.y - y) < apart)) continue;
                taken.push({ x, y, radius });
            }
        }
        if (taken.length >= n) break;
    }
    // Nowhere at all would stand on: a tile built kerb to kerb. Fall back to
    // the ring the team used before any of this existed — being on a roof beats
    // being in a heap on one spot, and it cannot happen on a generated map.
    while (taken.length < n) {
        const ang = phase + taken.length * 2.39996;
        taken.push({ x: around.x + Math.cos(ang) * 0.115, y: around.y + Math.sin(ang) * 0.115, radius: 0.115 });
    }
    return taken.slice(0, n);
}

/**
 * What a tile makes of the party: where it gathers, where each member stands,
 * and whether they are working or resting.
 *
 * The one place a room TYPE gets to change the choreography, and it changes it
 * by moving points around rather than by branching into a second animation:
 * a campsite rings its fire instead of quartering the ground, and the same
 * `gait` that settles a search party onto its posts sits this one down.
 *
 * @param plan   normalisePlan() of the tile's ground plan
 * @param count  team size
 * @param seed   stable per tile, so a room is laid out the same way every visit
 * @param roomType  the raid's own room type ('campsite', 'water', 'gate', ...)
 */
export function tileStage(plan, count, seed, roomType) {
    // A campsite is the one room the team stops AT rather than searches. It
    // needs a fire to gather round; a save written before tiles had plans, or
    // a region that draws its rest stop some other way, falls back to an
    // ordinary search and looks exactly as it did before.
    const camping = roomType === 'campsite' && !!plan?.focus;
    const posts = camping
        ? searchPosts(count, plan, seed, { around: plan.focus, rings: CAMP_RINGS, spacing: [0.075, 0.05, 0] })
        : searchPosts(count, plan, seed);
    const anchor = camping ? plan.focus : gatherPoint(plan);
    return {
        anchor,
        resting: camping,
        spread: postSpread(posts, anchor),
        // Posts are places ON THE TILE; what a pose wants is an offset from the
        // party, and the anchor sits between the two. Rebased once here rather
        // than in every drawn frame.
        postOffsets: posts.map(p => ({ x: p.x - anchor.x, y: p.y - anchor.y })),
    };
}

/**
 * The furthest any member has to walk to reach their post — measured from
 * where the party GATHERS, not from the middle of the tile.
 *
 * On a water tile the two are half a tile apart, so measuring from the middle
 * would have timed the fan-out for a walk nobody makes.
 */
export function postSpread(posts, anchor = null) {
    const ax = anchor?.x || 0, ay = anchor?.y || 0;
    let far = 0;
    for (const p of posts || []) far = Math.max(far, Math.hypot(p.x - ax, p.y - ay));
    return far || SPREAD_TILES;
}

/**
 * Where the party gathers on this tile: the nearest place to the middle of it
 * that somebody could actually stand.
 *
 * Almost always the middle itself, which is what the team has always used. It
 * matters on the two tiles where the middle is not ground: the water tile,
 * whose middle is the deep end of a lake, and a tile built over kerb to kerb.
 * Posts alone were not enough for those — they fixed where the team STANDS
 * while it searches, and left it wading in as a file to get there.
 *
 * Returned in tile fractions from the centre, so `{x: 0, y: 0}` means "the
 * middle is fine" and everything downstream can stay as it was.
 */
export function gatherPoint(plan) {
    if (standable(plan, 0, 0)) return { x: 0, y: 0 };
    for (const radius of POST_RINGS) {
        let best = null;
        for (let a = 0; a < POST_ANGLES * 2; a++) {
            const ang = (a / (POST_ANGLES * 2)) * TAU;
            const x = Math.cos(ang) * radius, y = Math.sin(ang) * radius;
            if (!standable(plan, x, y)) continue;
            // The shallowest way out: whichever direction reaches ground first
            if (!best || Math.hypot(x, y) < Math.hypot(best.x, best.y)) best = { x, y };
        }
        if (best) return best;
    }
    return { x: 0, y: 0 };   // nowhere to stand at all; see searchPosts
}

// Tiles per millisecond, from the one constant that sets the pace.
const WALK_SPEED = 0.5 / WALK_HALF_MS;

/**
 * Where one member stands relative to the party's centre, and where their
 * torch is pointing.
 *
 * @param i      member index
 * @param count  team size (1-3 in practice — the roster caps at three)
 * @param tSec   animation time, seconds
 * @param party  what partyAt returned
 * @param seed   any integer; decorrelates two members' wander and sweep
 * @param post   this member's standing post from searchPosts(), or null for
 *               the plain ring the team used before tiles had ground plans
 * @returns {{dx, dy, facing, stride, gait}}
 *   dx/dy   offset from the party centre in TILE FRACTIONS
 *   facing  radians; the torch direction
 *   stride  -1..1 gait cycle, for the leg swing and body bob
 *
 * Two arrangements blended by gait: file astern while marching, and their
 * posts while searching. Blending rather than switching is what stops three
 * figures snapping into place the instant the party stops walking.
 *
 * **Only the marching formation is rotated by the party's heading**, and that
 * is not a detail. `heading` turns across the tile as the path does, so
 * anything rotated by it moves whenever the path bends; the file has to (a
 * team walking north trails north-to-south) and the posts must not, because a
 * post is a PLACE — a doorway, a patch of bank — and places do not swing round
 * when the party's bearing changes. Rotating them also used to leave `sweep`
 * pointing off-axis, since it was computed from the unrotated angle.
 */
export function memberPose(i, count, tSec, party, seed = 0, post = null) {
    // golden-angle spacing plus the seed: two members are never in step, and
    // the same member is in the same place every frame
    const ph = i * 2.39996 + (seed % 997) * 0.0063;
    // Eased rather than linear: the settle starts and ends at rest, so the
    // formation change reads as three people spreading out rather than three
    // people being slid into place at a constant rate.
    const gait = smooth(clamp01(party?.gait ?? 0));
    const heading = party?.heading ?? 0;

    // --- marching: single file, staggered a little off the centreline so the
    // one behind is not hidden by the one in front. Authored in the party's
    // own frame (forward/right) and rotated into grid space below.
    const rank = Math.floor((i + 1) / 2);
    const mf = -0.085 * rank;
    const mr = i === 0 ? 0 : 0.05 * (i % 2 ? -1 : 1);
    const c = Math.cos(heading), s = Math.sin(heading);
    const mx = mf * c - mr * s;
    const my = mf * s + mr * c;

    // --- searching: their post, with a small amount of shifting about on it.
    // The wander is a fraction of the clearance a post is chosen with, so
    // pottering can never carry anyone into the wall they are standing beside.
    const px = post ? post.x : Math.cos(ph + tSec * 0.10) * 0.115;
    const py = post ? post.y : Math.sin(ph + tSec * 0.10) * 0.115;
    const sx = px + 0.016 * Math.sin(tSec * 0.29 + ph);
    const sy = py + 0.016 * Math.sin(tSec * 0.23 + ph * 1.7);

    // The torch leads where they walk and sweeps where they search. The sweep
    // is centred on the way they face out of the tile's middle, so three
    // members between them cover the ground instead of all raking one corner.
    const sweep = Math.atan2(sy, sx) + Math.sin(tSec * 0.55 + ph) * 0.9;

    return {
        dx: mix(sx, mx, gait),
        dy: mix(sy, my, gait),
        facing: mixAngle(sweep, heading, gait),
        // a two-beat walk cycle, decaying to a slow shift of weight on the spot
        stride: gait * Math.sin(tSec * 5.6 + ph) + (1 - gait) * 0.16 * Math.sin(tSec * 0.9 + ph),
        gait,
    };
}
